import { useEffect, useMemo, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { useSettings } from "context/SettingsContext";
import { useTheme } from "context/ThemeContext";
import { useToast } from "context/ToastContext";
import * as apiObject from "api/apiObjects";
import { selectStylesDark, selectStylesLigth } from "utils/helpers/structs/selectStyles";
import spinnerTitle from "components/others/spinnerTitle";
import SvgSave from "components/svg/SvgSave";

const parseObjectDetails = (rawValue) => {
    if (!rawValue) return {};
    if (typeof rawValue === "object" && !Array.isArray(rawValue)) return rawValue;
    if (typeof rawValue !== "string") return {};

    try {
        const parsed = JSON.parse(rawValue);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch (_e) {
        return {};
    }
};

const getFieldKey = (field, idx) => {
    const base = (field?.i18nKey || field?.title || `field_${idx + 1}`).toString().trim();
    return base || `field_${idx + 1}`;
};

const ObjectDetails = ({
    objectId = null,
    objectDetails = null,
    readOnly = false,
    onSaved = null,
    saveButtonClassName = "pt-3 text-center",
}) => {
    const { t } = useTranslation(["Objects", "Settings", "common"]);
    const { darkTheme } = useTheme();
    const { showToast } = useToast();
    const { settings, localSettings } = useSettings();
    const [formValues, setFormValues] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    const modeFields = useMemo(() => {
        if (Array.isArray(localSettings?.modeFields)) return localSettings.modeFields;
        if (Array.isArray(settings?.modeFields)) return settings.modeFields;
        return [];
    }, [localSettings?.modeFields, settings?.modeFields]);

    useEffect(() => {
        setFormValues(parseObjectDetails(objectDetails));
    }, [objectDetails]);

    const handleValueChange = (fieldKey, value) => {
        setFormValues((prev) => ({
            ...prev,
            [fieldKey]: value,
        }));
    };

    const getDisplayValue = (value) => {
        if (Array.isArray(value)) {
            const text = value.filter(Boolean).join(", ");
            return text || "-";
        }
        if (value === null || value === undefined || value === "") return "-";
        return String(value);
    };

    const normalizePayload = () => {
        const normalized = {};
        modeFields.forEach((field, idx) => {
            const fieldKey = getFieldKey(field, idx);
            const rawValue = formValues?.[fieldKey];

            if (field?.type === "number") {
                if (rawValue === "" || rawValue === null || rawValue === undefined) {
                    normalized[fieldKey] = null;
                } else {
                    const parsed = Number(rawValue);
                    normalized[fieldKey] = Number.isFinite(parsed) ? parsed : null;
                }
                return;
            }

            if (field?.type === "select-multi") {
                normalized[fieldKey] = Array.isArray(rawValue)
                    ? rawValue.map((item) => String(item || "").trim()).filter(Boolean)
                    : [];
                return;
            }

            normalized[fieldKey] = rawValue === null || rawValue === undefined ? "" : String(rawValue);
        });
        return normalized;
    };

    const handleSave = async () => {
        if (!objectId || readOnly || isSaving) return;
        setIsSaving(true);
        const payload = normalizePayload();
        const res = await apiObject.setDetails(objectId, payload);
        if (res) {
            showToast(t("messSpecificationSaved"), "success");
            await onSaved?.();
        } else {
            showToast(t("common:messError"), "danger");
        }
        setIsSaving(false);
    };

    if (!modeFields.length) {
        return <p className="text-muted fs-12 mb-0 px-3">{t("specification.emptyByMode")}</p>;
    }
 
    return (
        <div className="px-3">
            {modeFields.map((field, idx) => {
                const fieldKey = getFieldKey(field, idx);
                const labelByI18n = field?.i18nKey
                    ? t(`Settings:businessMode.fieldLabels.${field.i18nKey}`, { defaultValue: "" })
                    : "";
                const label = labelByI18n || field?.title || `${t("specification.field")} #${idx + 1}`;
                const value = formValues?.[fieldKey];

                return (
                    <Form.Group className="mb-3" key={fieldKey}>
                        <Form.Label className="mb-1">{label}</Form.Label>
                        {readOnly ? (
                            <div className="spec-field-value mb-0 small bg-light p-2 rounded border text-break">
                                {getDisplayValue(value)}
                            </div>
                        ) : null}

                        {!readOnly && field?.type === "text" ? (
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={value || ""}
                                onChange={(e) => handleValueChange(fieldKey, e.target.value)}
                            />
                        ) : null}

                        {!readOnly && field?.type === "number" ? (
                            <Form.Control
                                type="number"
                                value={value ?? ""}
                                onChange={(e) => handleValueChange(fieldKey, e.target.value)}
                            />
                        ) : null}

                        {!readOnly && field?.type === "select" ? (
                            <Form.Select
                                value={value || ""}
                                onChange={(e) => handleValueChange(fieldKey, e.target.value)}
                            >
                                <option value="">{t("specification.selectPlaceholder")}</option>
                                {(field?.selectValues || []).map((option) => (
                                    <option key={`${fieldKey}-${option}`} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </Form.Select>
                        ) : null}

                        {!readOnly && field?.type === "select-multi" ? (
                            <Select
                                isMulti={true}
                                options={(field?.selectValues || []).map((option) => ({ value: option, label: option }))}
                                styles={{
                                    ...(darkTheme ? selectStylesDark : selectStylesLigth),
                                    control: (base, state) => ({
                                        ...base,
                                        ...((darkTheme ? selectStylesDark : selectStylesLigth).control?.(base, state) || {}),
                                    }),
                                }}
                                value={(Array.isArray(value) ? value : []).map((item) => ({ value: item, label: item }))}
                                onChange={(selected) =>
                                    handleValueChange(
                                        fieldKey,
                                        Array.isArray(selected) ? selected.map((item) => item.value) : [],
                                    )
                                }
                                placeholder={t("specification.selectPlaceholder")}
                            />
                        ) : null}

                        {!readOnly && field?.type === "date" ? (
                            <Form.Control
                                type="date"
                                value={value || ""}
                                onChange={(e) => handleValueChange(fieldKey, e.target.value)}
                            />
                        ) : null}

                        {!readOnly && !["text", "number", "select", "select-multi", "date"].includes(field?.type) ? (
                            <Form.Control
                                type="text"
                                value={value || ""}
                                onChange={(e) => handleValueChange(fieldKey, e.target.value)}
                            />
                        ) : null}
                    </Form.Group>
                );
            })}

            {!readOnly ? (
                <div className={saveButtonClassName}>
                    <Button onClick={handleSave} disabled={isSaving || !objectId}>
                        {spinnerTitle(
                            <>
                                <SvgSave className="me-2 mtop-2" />
                                {t("common:buttons.save")}
                            </>,
                            isSaving,
                            t("common:buttons.saving"),
                        )}
                    </Button>
                </div>
            ) : null}
        </div>
    );
};

export default ObjectDetails;
